import { useCallback } from 'react';
import { useParams } from 'react-router';

import { useRenewSubscriptionMutation, useUpdateRenewInformationMutation } from 'store/api/version-api.slice';
import { formatRenewalDescription, getMemberCustomerIdOrEmail, getMemberTypeId } from 'utils';

import useDispatchMessage from './useDispatchMessage';

const useRenewWithCardOnFileStripe = ({ setPaymentStatus, renewInfo, memberTypes }) => {
	const params = useParams();

	const dispatchMessage = useDispatchMessage();

	const [renewSubscriptionWithCardOnFile, { isLoading: isRenewLoading }] = useRenewSubscriptionMutation();
	const [updatePaymentIntent] = useUpdateRenewInformationMutation();

	const handler = useCallback(
		async data => {
			try {
				const updateResult = await updatePaymentIntent({
					portalId: params.portal_id,
					contactId: params.contact_id,
					data: {
						member_type_id: getMemberTypeId({
							memberTypeName: data.member_type,
							memberTypePlans: memberTypes
						}),
						payment_intent_id: renewInfo.payment_intent.id
					}
				});

				const renewData = {
					payment_intent_id: updateResult.data.id,
					payment_method_id: data.card_on_file?.details.id,
					...getMemberCustomerIdOrEmail({
						customerId: data.card_on_file?.details.customer,
						email: renewInfo.member?.email
					}),
					member_type_id: getMemberTypeId({ memberTypeName: data.member_type, memberTypePlans: memberTypes }),
					return_url: window.location.href,
					description: formatRenewalDescription({
						email: renewInfo.member?.email,
						memberTypeName: data.member_type
					})
				};

				const result = await renewSubscriptionWithCardOnFile({
					portalId: params.portal_id,
					contactId: params.contact_id,
					data: renewData
				});

				if (result?.data.status === 'succeeded') {
					const { currency, amount } = result.data;
					const format_amount = (amount / 100).toFixed(2);
					const message = `Your payment of ${format_amount} ${currency.toUpperCase()} has been processed successfully`;

					dispatchMessage({ message });
					setPaymentStatus('SUCCESS');
				} else {
					dispatchMessage({
						message: 'Payment Failed! Please, try again later',
						variant: 'error'
					});
					setPaymentStatus('ERROR');
				}
			} catch (e) {
				dispatchMessage({
					message: 'Payment Failed! Please, try again later',
					variant: 'error'
				});
				setPaymentStatus('ERROR');
			}
		},
		[
			dispatchMessage,
			memberTypes,
			params.contact_id,
			params.portal_id,
			renewInfo.member?.email,
			renewInfo.payment_intent?.id,
			renewSubscriptionWithCardOnFile,
			setPaymentStatus,
			updatePaymentIntent
		]
	);

	return { handler, isRenewLoading };
};

export default useRenewWithCardOnFileStripe;
